import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, firstValueFrom } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { TrackerEvent, TrackerCode } from '@models/tracker-event.model';
import { ENVIRONMENT } from '@environments/environment';
import { TokenService } from './token.service';
import { Logger } from '@utils/logger.util';

@Injectable({
    providedIn: 'root'
})
export class TrackerService {
    private serviceUrl = ENVIRONMENT.BASE_URL_API;
    private enabled = true; // Puede deshabilitarse en desarrollo

    constructor(
        private http: HttpClient,
        private tokenService: TokenService
    ) {}

    /**
     * Envía un evento de tracking al servidor
     * @param event Evento a trackear
     * @param transactionId ID de transacción opcional
     */
    private trackerEvent(event: TrackerEvent, transactionId?: string): Observable<any> {
        if (!this.enabled) {
            Logger.debug('Tracker deshabilitado:', event);
            return new Observable(observer => {
                observer.next({ success: true, disabled: true });
                observer.complete();
            });
        }

        const url = `${this.serviceUrl}/tracker/v1/track`;
        let headers = new HttpHeaders().append('Content-Type', 'application/json');

        // Agregar transaction-id si existe
        if (transactionId) {
            headers = headers.append('transaction-id', transactionId);
        } else {
            console.warn('⚠️ Tracker: No se proporcionó transactionId');
        }

        // Agregar información del usuario si está disponible
        const tokenValues = this.tokenService.getTokenValues();
        if (tokenValues && !event.userId) {
            event.userId = tokenValues.sub || (tokenValues as any).email || 'unknown';
        }

        return this.http.post<any>(url, event, { headers }).pipe(
            timeout(10000),
            catchError(this.handleError)
        );
    }

    /**
     * Genera y envía un evento de tracking en una sola operación
     */
    trackEvent(event: Partial<TrackerEvent>, transactionId?: string): Observable<any> {
        const trackerEvent: TrackerEvent = {
            source: 'MAINTAINER-UI',
            timestamp: new Date().toISOString(),
            ...event
        } as TrackerEvent;

        return this.trackerEvent(trackerEvent, transactionId);
    }

    /**
     * Método async/await para enviar eventos de tracking
     */
    async trackEventAsync(event: Partial<TrackerEvent>, transactionId?: string): Promise<any> {
        try {
            const response = await firstValueFrom(this.trackEvent(event, transactionId));
            return response;
        } catch (error) {
            Logger.error('Tracker: Error enviando evento:', error);
            // No lanzar error para no interrumpir el flujo de la aplicación
            return null;
        }
    }

    // ==================== HELPERS POR TIPO DE EVENTO ====================

    /**
     * Helper para eventos de PAYMENT
     */
    async trackPaymentEvent(
        type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO',
        code: string | TrackerCode,
        transactionId?: string,
        additionalData?: any
    ): Promise<any> {
        return this.trackEventAsync({
            kind: 'PAYMENT',
            type,
            code: code.toString(),
            ...(additionalData && { data: additionalData })
        }, transactionId);
    }

    /**
     * Helper para eventos de CUSTOMER
     */
    async trackCustomerEvent(
        type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO',
        code: string | TrackerCode,
        customerId?: string,
        additionalData?: any
    ): Promise<any> {
        return this.trackEventAsync({
            kind: 'CUSTOMER',
            type,
            code: code.toString(),
            ...(additionalData && { data: additionalData })
        }, customerId);
    }

    /**
     * Helper para eventos de AUTH
     */
    async trackAuthEvent(
        type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO',
        code: string | TrackerCode,
        additionalData?: any
    ): Promise<any> {
        return this.trackEventAsync({
            kind: 'AUTH',
            type,
            code: code.toString(),
            ...(additionalData && { data: additionalData })
        });
    }

    /**
     * Helper para eventos de MERCHANT
     */
    async trackMerchantEvent(
        type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO',
        code: string | TrackerCode,
        merchantId?: string,
        additionalData?: any
    ): Promise<any> {
        return this.trackEventAsync({
            kind: 'MERCHANT',
            type,
            code: code.toString(),
            ...(additionalData && { data: additionalData })
        }, merchantId);
    }

    /**
     * Helper para eventos de USER
     */
    async trackUserEvent(
        type: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO',
        code: string | TrackerCode,
        userId?: string,
        additionalData?: any
    ): Promise<any> {
        return this.trackEventAsync({
            kind: 'USER',
            type,
            code: code.toString(),
            userId,
            ...(additionalData && { data: additionalData })
        });
    }

    /**
     * Helper para eventos de ERROR del sistema
     */
    async trackError(
        code: string | TrackerCode,
        error: any,
        context?: string
    ): Promise<any> {
        const errorData = {
            message: error?.message || error?.toString() || 'Unknown error',
            context,
            stack: error?.stack,
            status: error?.status,
            url: error?.url
        };

        return this.trackEventAsync({
            kind: 'ERROR',
            type: 'ERROR',
            code: code.toString(),
            data: errorData
        });
    }

    /**
     * Habilita o deshabilita el tracking
     */
    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        Logger.log(`Tracker ${enabled ? 'habilitado' : 'deshabilitado'}`);
    }

    /**
     * Manejo de errores
     */
    private handleError(error: any): Observable<never> {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
            // Error del lado del cliente
            errorMessage = `Error: ${error.error.message}`;
        } else {
            // Error del lado del servidor
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }
        Logger.error('Tracker service error:', errorMessage);
        return throwError(() => errorMessage);
    }
}

